import { useEffect, useState, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { LogOut, Search, Mail, Phone, MapPin, ExternalLink, Trash2, CheckCircle2, Clock, Archive, RefreshCw, Download } from "lucide-react";
import { API, useAuth } from "@/App";

const LOGO = "/assets/cravy-logo.png";
const STATUSES = [
  { key: "all",       label: "All",       color: "bg-cravy-cream/10 text-cravy-cream/80" },
  { key: "new",       label: "New",       color: "bg-cravy-orange/20 text-cravy-orange" },
  { key: "contacted", label: "Contacted", color: "bg-sky-400/15 text-sky-300" },
  { key: "won",       label: "Won",       color: "bg-emerald-400/15 text-emerald-300" },
  { key: "archived",  label: "Archived",  color: "bg-cravy-cream/8 text-cravy-cream/50" },
];

function authHeaders() {
  const token = localStorage.getItem("cravy_admin_token");
  return { Authorization: `Bearer ${token}` };
}

export default function AdminDashboard() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [leads, setLeads] = useState([]);
  const [stats, setStats] = useState({ total: 0, new: 0, contacted: 0, won: 0 });
  const [filter, setFilter] = useState("all");
  const [search, setSearch] = useState("");
  const [selected, setSelected] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [{ data: ls }, { data: st }] = await Promise.all([
        axios.get(`${API}/admin/leads`, { headers: authHeaders() }),
        axios.get(`${API}/admin/stats`, { headers: authHeaders() }),
      ]);
      setLeads(ls);
      setStats(st);
    } catch (err) {
      if (err?.response?.status === 401) {
        logout(); navigate("/admin/login", { replace: true });
      } else {
        toast.error("Could not load leads.");
      }
    } finally {
      setLoading(false);
    }
  }, [logout, navigate]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => { document.title = "Cravy — Admin Console"; }, []);

  const filtered = leads.filter((l) => {
    if (filter !== "all" && l.status !== filter) return false;
    if (!search) return true;
    const s = search.toLowerCase();
    return (
      l.property_name?.toLowerCase().includes(s) ||
      l.contact_name?.toLowerCase().includes(s) ||
      l.email?.toLowerCase().includes(s) ||
      l.property_type?.toLowerCase().includes(s)
    );
  });

  const updateStatus = async (id, status) => {
    try {
      const { data } = await axios.patch(`${API}/admin/leads/${id}`, { status }, { headers: authHeaders() });
      setLeads((prev) => prev.map((l) => (l.id === id ? data : l)));
      if (selected?.id === id) setSelected(data);
      load();
      toast.success(`Marked as ${status}.`);
    } catch {
      toast.error("Could not update status.");
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this lead permanently?")) return;
    try {
      await axios.delete(`${API}/admin/leads/${id}`, { headers: authHeaders() });
      setLeads((prev) => prev.filter((l) => l.id !== id));
      if (selected?.id === id) setSelected(null);
      load();
      toast.success("Lead deleted.");
    } catch {
      toast.error("Could not delete.");
    }
  };

  const exportCsv = async () => {
    try {
      const resp = await axios.get(`${API}/admin/leads/export.csv`, {
        headers: authHeaders(),
        responseType: "blob",
      });
      const url = window.URL.createObjectURL(new Blob([resp.data], { type: "text/csv" }));
      const a = document.createElement("a");
      a.href = url;
      const ts = new Date().toISOString().slice(0, 16).replace(/[-:T]/g, "");
      a.download = `cravy-leads-${ts}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success("CSV exported.");
    } catch {
      toast.error("Could not export CSV.");
    }
  };

  const statusPill = (s) => {
    const cfg = STATUSES.find((x) => x.key === s) || STATUSES[1];
    return <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-[11px] font-semibold uppercase tracking-wider ${cfg.color}`}>{cfg.label}</span>;
  };

  const fmtDate = (iso) => {
    try { return new Date(iso).toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" }); }
    catch { return iso; }
  };

  return (
    <div className="min-h-screen bg-cravy-ink cravy-grain text-cravy-cream font-body">
      {/* Top bar */}
      <header className="border-b border-white/5 bg-cravy-ink/90 backdrop-blur-md sticky top-0 z-30">
        <div className="max-w-[1400px] mx-auto px-5 lg:px-8 h-[68px] flex items-center justify-between">
          <Link to="/" className="flex items-center" data-testid="admin-logo">
            <img src={LOGO} alt="Cravy" className="h-11 w-11 object-contain" />
          </Link>
          <div className="flex items-center gap-3">
            <button onClick={exportCsv} className="btn-ghost-dark px-4 py-2 rounded-full text-[13px] inline-flex items-center gap-2" data-testid="admin-export-csv">
              <Download className="w-4 h-4" /> Export CSV
            </button>
            <button onClick={load} className="btn-ghost-dark px-4 py-2 rounded-full text-[13px] inline-flex items-center gap-2" data-testid="admin-refresh">
              <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} /> Refresh
            </button>
            <div className="hidden sm:block text-cravy-cream/55 text-[13px]">{user?.email}</div>
            <button onClick={() => { logout(); navigate("/admin/login"); }} className="btn-ghost-dark px-4 py-2 rounded-full text-[13px] inline-flex items-center gap-2" data-testid="admin-logout">
              <LogOut className="w-4 h-4" /> Sign out
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-[1400px] mx-auto px-5 lg:px-8 py-10">
        {/* Stats */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
          {[
            { k: "Total leads",      v: stats.total,      icon: Mail,         tone: "text-cravy-cream" },
            { k: "New",              v: stats.new,        icon: Clock,        tone: "text-cravy-orange" },
            { k: "In conversation",  v: stats.contacted,  icon: ExternalLink, tone: "text-sky-300" },
            { k: "Won",              v: stats.won,        icon: CheckCircle2, tone: "text-emerald-300" },
          ].map((s, i) => (
            <div key={i} className="rounded-2xl border border-white/8 bg-white/[0.03] p-5" data-testid={`admin-stat-${s.k.toLowerCase().replace(/\s+/g,'-')}`}>
              <div className="flex items-center justify-between">
                <div className="text-cravy-cream/55 text-[12px] uppercase tracking-[0.15em]">{s.k}</div>
                <s.icon className={`w-4 h-4 ${s.tone}`} />
              </div>
              <div className="mt-3 font-display font-extrabold text-[36px] leading-none">{s.v}</div>
            </div>
          ))}
        </div>

        {/* Filter + Search */}
        <div className="flex flex-col sm:flex-row gap-3 mb-5">
          <div className="flex flex-wrap items-center gap-2">
            {STATUSES.map((s) => (
              <button
                key={s.key}
                onClick={() => setFilter(s.key)}
                className={`px-4 py-2 rounded-full text-[13px] font-semibold transition border ${
                  filter === s.key
                    ? "bg-cravy-orange text-cravy-ink border-cravy-orange"
                    : "border-white/10 text-cravy-cream/70 hover:text-cravy-cream hover:border-white/30"
                }`}
                data-testid={`filter-${s.key}`}
              >
                {s.label}
              </button>
            ))}
          </div>
          <div className="flex-1 sm:max-w-sm sm:ml-auto relative">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-cravy-cream/40" />
            <input
              placeholder="Search leads…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="cravy-input pl-11"
              data-testid="admin-search"
            />
          </div>
        </div>

        {/* List + Detail */}
        <div className="grid lg:grid-cols-12 gap-5">
          <div className="lg:col-span-7 space-y-3">
            {loading ? (
              <div className="text-cravy-cream/50 text-center py-16">Loading leads…</div>
            ) : filtered.length === 0 ? (
              <div className="rounded-3xl border border-dashed border-white/10 p-12 text-center text-cravy-cream/50" data-testid="admin-empty">
                No leads here yet. They'll show up the moment a property submits the form.
              </div>
            ) : (
              filtered.map((l) => (
                <button
                  key={l.id}
                  onClick={() => setSelected(l)}
                  className={`w-full text-left rounded-2xl border p-5 transition-all hover:-translate-y-0.5 ${
                    selected?.id === l.id
                      ? "border-cravy-orange/60 bg-white/[0.06]"
                      : "border-white/8 bg-white/[0.03] hover:border-white/20"
                  }`}
                  data-testid={`lead-card-${l.id}`}
                >
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <div className="font-display font-bold text-[18px] tracking-tight">{l.property_name}</div>
                      <div className="text-cravy-cream/60 text-[13px] mt-0.5">{l.contact_name} · {l.property_type}</div>
                    </div>
                    {statusPill(l.status)}
                  </div>
                  <div className="mt-4 flex flex-wrap items-center gap-x-5 gap-y-2 text-[13px] text-cravy-cream/55">
                    <span className="inline-flex items-center gap-1.5"><Mail className="w-3.5 h-3.5" /> {l.email}</span>
                    {l.phone && <span className="inline-flex items-center gap-1.5"><Phone className="w-3.5 h-3.5" /> {l.phone}</span>}
                    {l.address && <span className="inline-flex items-center gap-1.5"><MapPin className="w-3.5 h-3.5" /> {l.address}</span>}
                  </div>
                  <div className="mt-3 text-cravy-cream/40 text-[12px]">{fmtDate(l.created_at)}</div>
                </button>
              ))
            )}
          </div>

          {/* Detail */}
          <aside className="lg:col-span-5">
            <div className="rounded-3xl border border-white/8 bg-white/[0.03] p-6 lg:sticky lg:top-[88px]" data-testid="admin-detail">
              {!selected ? (
                <div className="text-center py-14 text-cravy-cream/45 text-[14px]">
                  Pick a lead to see all the details and update its status.
                </div>
              ) : (
                <>
                  <div className="flex items-start justify-between gap-4">
                    <div>
                      <div className="font-display font-extrabold text-[24px] tracking-tight">{selected.property_name}</div>
                      <div className="text-cravy-cream/55 text-[13px] mt-1">{fmtDate(selected.created_at)}</div>
                    </div>
                    {statusPill(selected.status)}
                  </div>

                  <div className="mt-6 space-y-3 text-[14px]">
                    <DetailRow label="Contact"        value={selected.contact_name} />
                    <DetailRow label="Property type"  value={selected.property_type} />
                    <DetailRow label="Email"          value={<a className="text-cravy-orange hover:underline" href={`mailto:${selected.email}`}>{selected.email}</a>} />
                    <DetailRow label="Phone"          value={selected.phone ? <a className="hover:underline" href={`tel:${selected.phone}`}>{selected.phone}</a> : "—"} />
                    <DetailRow label="Address"        value={selected.address || "—"} />
                    <DetailRow label="Occupancy"      value={selected.occupancy || "—"} />
                    <DetailRow label="Foot traffic"   value={selected.foot_traffic || "—"} />
                    <DetailRow label="Spot / Notes"   value={selected.message || "—"} />
                  </div>

                  <div className="mt-7 border-t border-white/10 pt-5">
                    <div className="text-cravy-cream/55 text-[11px] uppercase tracking-[0.2em] mb-3">Update status</div>
                    <div className="flex flex-wrap gap-2">
                      {STATUSES.filter((s) => s.key !== "all").map((s) => (
                        <button
                          key={s.key}
                          onClick={() => updateStatus(selected.id, s.key)}
                          disabled={selected.status === s.key}
                          className={`px-3.5 py-2 rounded-full text-[12px] font-semibold transition border ${
                            selected.status === s.key
                              ? "bg-cravy-orange text-cravy-ink border-cravy-orange cursor-default"
                              : "border-white/15 text-cravy-cream/80 hover:border-cravy-orange hover:text-cravy-orange"
                          }`}
                          data-testid={`status-btn-${s.key}`}
                        >
                          {s.key === "won" ? <CheckCircle2 className="w-3.5 h-3.5 inline mr-1.5 -mt-0.5" /> :
                           s.key === "archived" ? <Archive className="w-3.5 h-3.5 inline mr-1.5 -mt-0.5" /> : null}
                          {s.label}
                        </button>
                      ))}
                    </div>

                    <div className="mt-6 flex flex-wrap gap-2">
                      <a href={`mailto:${selected.email}`} className="btn-orange inline-flex items-center gap-2 px-4 py-2.5 rounded-full text-[13px]" data-testid="action-email">
                        <Mail className="w-4 h-4" /> Email
                      </a>
                      {selected.phone && (
                        <a href={`tel:${selected.phone}`} className="btn-ghost-dark inline-flex items-center gap-2 px-4 py-2.5 rounded-full text-[13px]" data-testid="action-call">
                          <Phone className="w-4 h-4" /> Call
                        </a>
                      )}
                      <button onClick={() => remove(selected.id)} className="ml-auto inline-flex items-center gap-2 px-4 py-2.5 rounded-full text-[13px] border border-red-500/30 text-red-300 hover:bg-red-500/10 transition" data-testid="action-delete">
                        <Trash2 className="w-4 h-4" /> Delete
                      </button>
                    </div>
                  </div>
                </>
              )}
            </div>
          </aside>
        </div>
      </main>
    </div>
  );
}

function DetailRow({ label, value }) {
  return (
    <div className="grid grid-cols-3 gap-3 items-start">
      <div className="text-cravy-cream/45 text-[11px] uppercase tracking-[0.15em] pt-0.5">{label}</div>
      <div className="col-span-2 text-cravy-cream/90 break-words">{value}</div>
    </div>
  );
}
